import { computed, DestroyRef, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { BehaviorSubject, firstValueFrom, forkJoin, Observable, of, Subscription } from 'rxjs';
import { RealEstate as RealEstateV2, ServiceCompany, ServiceCompanyService, TenantUnitDetail } from '@eadsysteme/ewsrealestateclient-v2';
import { KeycloakService } from 'src/app/auth/keycloak.service';
import { AuthService, PermissionGroup, PermissionObject } from '@eadsysteme/ewsauthclient-v1';
import { Router } from '@angular/router';
import { Contact, ContactService } from '@eadsysteme/contactclient-v1';
import { SimpleRealEstate } from '../util/simpleEntities/SimpleRealEstate';
import { SettingControllerService } from '@eadsysteme/settingclient-v1';
import { SimpleEntityMapper } from './simple-entity-mapper.service';
import { ContactWithArea } from '../util/ContactWithArea';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { KeycloakTokenParsed } from 'keycloak-js';
import { ServiceCompanyDesignService } from '@eadsysteme/portalsettingclient-v1';
import { catchError, map, tap } from 'rxjs/operators';
import { KEYCLOAK_EVENT_SIGNAL } from 'keycloak-angular';

@Injectable({
	providedIn: 'root',
})
export class SessionService {
	private keycloakService = inject(KeycloakService);
	private authService = inject(AuthService);
	private route = inject(Router);
	private contactService = inject(ContactService);
	private readonly serviceCompanyService = inject(ServiceCompanyService);
	private simpleEntityMapperService = inject(SimpleEntityMapper);
	private settingService = inject(SettingControllerService);

	private serviceCompanyDesignService = inject(ServiceCompanyDesignService);
	private readonly keycloakEvent = inject(KEYCLOAK_EVENT_SIGNAL);

	private readonly _arasys = 'arasys.';
	private destroyRef = inject(DestroyRef);
	// TODO Rework variables to signals!
	currentRealEstate: SimpleRealEstate;
	selectedPropertyManagementId = '';
	currentTenantUnit: TenantUnitDetail;
	userHasPermission: BehaviorSubject<boolean> = new BehaviorSubject(false);
	// TODO Remove currentArea
	keycloakUserID: string;
	keycloakUsername: string;
	sideNavWidth = 0;
	isPropertyManagement: WritableSignal<boolean> = signal(false);
	isServiceProvider: WritableSignal<boolean> = signal(false);
	isDevRole: WritableSignal<boolean> = signal(false);
	isSupportRole: WritableSignal<boolean> = signal(false);
	isBetaRole: WritableSignal<boolean> = signal(false);
	isNewNavigation: WritableSignal<boolean> = signal(false);
	hk4Enabled = false;
	isImpersonatedSession = false;
	selectedTenantUnitRolePermission: PermissionObject = null;
	/** Der Nutzer hat mindestens eine Berechtigung vom Typ PROPERTY_MANAGEMENT aus dem ewsauthWS. */
	readonly hasPropertyManagementPermission: WritableSignal<boolean> = signal(false);
	private currentArea: number;
	private readonly SERVICE_COMPANY_KEYCLOAK_ROLE = 'service_company';
	private realEstateSource: BehaviorSubject<SimpleRealEstate> = new BehaviorSubject({
		id: '',
		area: 0,
		number: 0,
	});
	private serviceCompanySource: BehaviorSubject<ServiceCompany> = new BehaviorSubject({ id: '' });
	private propertyManagementSource: BehaviorSubject<Contact> = new BehaviorSubject({ id: '', accountNumber: '' });
	selectedPropertyManagement$: Observable<Contact> = this.propertyManagementSource.asObservable();
	private tenantUnitSource: BehaviorSubject<TenantUnitDetail> = new BehaviorSubject({
		id: '',
		number: 0,
		subNumber: 0,
	});
	public currentAreas: number[];
	readonly tokenAreas = computed(
		() => {
			this.keycloakEvent();
			return this.readTokenAreas();
		},
		{ equal: (prev, next) => prev.length === next.length && prev.every((area, index) => area === next[index]) }
	);

	public propertyManagementContacts = signal<Contact[]>([]);
	permissionGroups: PermissionGroup[];
	cachedFavIconsByArea = new Map<number, string>();
	cachedServiceCompanyNames = new Map<number, string>();
	private leadingSystemSubscription: Subscription;

	constructor() {
		const tokenParsed: any = this.keycloakService.getKeycloakInstance().tokenParsed;
		this.currentArea = Number.parseInt(localStorage.getItem('currentArea') ?? '0', 10);
		this.updateSession(false);
		this.loadRoleInformation(tokenParsed);
	}

	public setPersistentArea(areaParam: number = this.currentArea) {
		this.currentArea = areaParam;
		localStorage.setItem('currentArea', String(this.currentArea)); //persistent
	}

	// Effektive Area für Backend-Requests (Header `area`).
	// Bevorzugt die Area einer aktiv geöffneten Liegenschaft / eines Billings
	// (aus sessionStorage, gesetzt von real-estates-legacy/sidenav beim Klick);
	// fällt auf currentArea (Header-Dropdown) zurück, wenn kein RealEstate-Kontext aktiv ist.
	// Immer nutzbar — auch in Bereichen ohne RealEstate-Auswahl (Invoicing, Contracts, ...).
	public get effectiveArea(): number {
		const stored = sessionStorage.getItem('selectedRealEstateArea');
		const parsed = stored ? Number(stored) : NaN;
		return Number.isFinite(parsed) && parsed > 0 ? parsed : this.currentArea;
	}

	public set effectiveArea(area: number) {
		sessionStorage.setItem('selectedRealEstateArea', String(area)); // tab local
	}

	public setCurrentArea(areaParam: number = 0) {
		this.setSelectRealEstateV2(null);
		this.setSelectTenantUnit(null);
		if (areaParam !== 0) {
			this.setPersistentArea(areaParam);
		} else {
			if (!this.currentArea) {
				const tokenParsed: any = this.keycloakService.getKeycloakInstance().tokenParsed;
				this.loadRoleInformation(tokenParsed);
			}
		}
	}

	private loadRoleInformation(tokenParsed: any) {
		this.permissionGroups = tokenParsed.group_permissions;

		const roleTypes = [
			{
				attribute: this.normalizeRoleAttributes(tokenParsed.PROPERTYMANAGEMENT_UUID ?? tokenParsed.propertymanagement_uuid),
				signal: this.isPropertyManagement,
			},
			{
				attribute: this.normalizeRoleAttributes(tokenParsed.SERVICEPROVIDER_UUID ?? tokenParsed.serviceprovider_uuid),
				signal: this.isServiceProvider,
			},
			{
				attribute: this.normalizeRoleAttributes(tokenParsed.ROLE_UUID ?? tokenParsed.role_uuid),
				signal: null,
			},
		];

		for (const role of roleTypes) {
			role.signal?.set(role.attribute.length > 0);
		}

		const firstWithAttribute = roleTypes.find((r) => r.attribute.length > 0);
		if (firstWithAttribute) {
			this.setPersistentArea(this.extractAreaFromAttribute(firstWithAttribute.attribute[0]));
		} else {
			this.setPersistentArea(0);
		}
	}

	private extractAreaFromAttribute(attribute: string): number {
		const areaString = attribute.substring(0, attribute.indexOf('_'));
		return parseInt(areaString) || 0;
	}

	public normalizeRoleAttributes(input: string | string[]): string[] {
		if (!input) {
			return [];
		}
		if (Array.isArray(input)) {
			return input;
		}
		// Split by comma OR semicolon, with optional whitespace
		return input
			.split(/[,;]+/)
			.map((s) => s.trim())
			.filter(Boolean);
	}

	getCompanyNameFromUrl() {
		const currentHost = window.location.host;
		const subDomain = currentHost.substring(0, currentHost.lastIndexOf('.'));
		return subDomain == '' ? this._arasys : subDomain;
	}

	async updateSession(clearToken: boolean = true) {
		if (clearToken) {
			await this.keycloakService.updateToken(-1);
		}
		this.setCurrentArea();
		this.checkForPermissions();
		await this.checkForPropertyManagement();
		await this.checkForServiceProvider();
		this.isDevRole.set(this.keycloakService.getKeycloakInstance().hasRealmRole('dev'));
		this.isSupportRole.set(this.keycloakService.getKeycloakInstance().hasRealmRole('support'));
		this.isBetaRole.set(this.keycloakService.getKeycloakInstance().hasRealmRole('beta'));
		if (localStorage.getItem('newNavigation')) {
			this.isNewNavigation.set(localStorage.getItem('newNavigation') === 'true');
		}
		this.checkLeadingSystem();
	}

	public checkLeadingSystem() {
		this.leadingSystemSubscription?.unsubscribe();
		this.leadingSystemSubscription = this.settingService
			.getConfigValue(this.currentArea, 'portal', 'general', 'contactLeadingSystem')
			.subscribe((serviceCompanySetting) => {
				const contactLeadingSystem = serviceCompanySetting?.value;
				this.hk4Enabled = contactLeadingSystem === 'HK4';
			});
	}

	checkForPermissions() {
		if (!this.userHasPermission.getValue()) {
			this.getAllPermissionsOfLoggedInUser().subscribe(
				(permissionObjects) => {
					this.hasPropertyManagementPermission.set(
						permissionObjects.some((permission) => permission.permissionType === PermissionObject.PermissionTypeEnum.PROPERTY_MANAGEMENT)
					);
					if (permissionObjects.length > 0) {
						this.userHasPermission.next(true);
					}
				},
				(err) => {
					if (this.isDevRole()) {
						console.warn('Could not check users permissions: ' + err.message);
					}
				},
				() => {}
			);
		}
	}

	getAllPermissionsOfLoggedInUser(): Observable<Array<PermissionObject>> {
		const tokenParsed: KeycloakTokenParsed = this.keycloakService.getKeycloakInstance().tokenParsed;
		this.keycloakUserID = tokenParsed.sub;
		this.keycloakUsername = tokenParsed.email;
		if (!this.currentArea) {
			this.setCurrentArea();
		}
		return this.authService.getAllGroupPermissionsForUser(this.currentArea, this.keycloakUserID);
	}

	getKeycloakUserID() {
		if (!this.keycloakUserID) {
			const tokenParsed = this.keycloakService.getKeycloakInstance().tokenParsed;
			this.keycloakUserID = tokenParsed.sub;
			this.keycloakUsername = tokenParsed.email;
		}
		return this.keycloakUserID;
	}

	setSelectRealEstateV2(realEstate: RealEstateV2) {
		const simpleRealEstate: SimpleRealEstate = this.simpleEntityMapperService.realEstateV2ToSimpleRealEstate(realEstate);
		this.realEstateSource.next(simpleRealEstate);
		this.currentRealEstate = simpleRealEstate;
	}

	setSelectServiceCompany(serviceCompany: ServiceCompany) {
		this.serviceCompanySource.next(serviceCompany);
	}

	setSelectPropertyManagement(propertyManagement: Contact) {
		if (propertyManagement) {
			this.propertyManagementSource.next(propertyManagement);
			this.selectedPropertyManagementId = propertyManagement.id;
		}
	}

	setSelectTenantUnit(tenantUnit: TenantUnitDetail) {
		this.tenantUnitSource.next(tenantUnit);
		this.currentTenantUnit = tenantUnit;
	}

	setNavigationStyle(newNavigation) {
		this.isNewNavigation.set(newNavigation.checked);
		localStorage.setItem('newNavigation', newNavigation.checked);
		this.route.navigate(['/dashboard']).then(() => {
			window.location.reload();
		});
	}

	private async checkForContactType(contactType: 'propertyManagement' | 'serviceProvider') {
		const contacts = await this.getContactsByType(contactType);
		const signal = contactType === 'propertyManagement' ? this.isPropertyManagement : this.isServiceProvider;
		signal.set(contacts.length > 0);
	}

	public async getPropertyManagementContacts(): Promise<Contact[]> {
		return this.getContactsByType('propertyManagement');
	}

	public async getServiceProviderContacts(): Promise<Contact[]> {
		return this.getContactsByType('serviceProvider');
	}

	private async getContactsByType(contactType: 'propertyManagement' | 'serviceProvider'): Promise<Contact[]> {
		const idsAndAreas = this.getIdsAndAreasByType(contactType);

		// Use Promise.all for parallel requests instead of sequential
		const contactPromises = idsAndAreas.map(
			({ area, id }) => firstValueFrom(this.contactService.getContactById(area, id)).catch(() => null) // Handle errors gracefully
		);

		const contacts = await Promise.all(contactPromises);
		return contacts.filter((contact): contact is Contact => contact !== null);
	}

	private getIdsAndAreasByType(contactType: 'propertyManagement' | 'serviceProvider'): ContactWithArea[] {
		const tokenParsed: any = this.keycloakService.getKeycloakInstance().tokenParsed;
		const attributeKey = contactType === 'propertyManagement' ? 'PROPERTYMANAGEMENT_UUID' : 'SERVICEPROVIDER_UUID';
		const attributeKeyLower = attributeKey.toLowerCase();

		const attributes = this.normalizeRoleAttributes(tokenParsed[attributeKey] ?? tokenParsed[attributeKeyLower]);

		if (!attributes) {
			return [];
		}
		this.loadFaviconsByArea(attributes);
		this.loadServiceCompanyNames(attributes);

		return attributes.map((attribute) => {
			const [area, id] = attribute.split('_');
			return { area: parseInt(area), id, accountNumber: '' };
		});
	}

	public getPropertyManagementIdsAndAreas(): ContactWithArea[] {
		return this.getIdsAndAreasByType('propertyManagement');
	}

	public getServiceProviderIdsAndAreas(): ContactWithArea[] {
		return this.getIdsAndAreasByType('serviceProvider');
	}

	private async checkForPropertyManagement() {
		await this.checkForContactType('propertyManagement');
	}

	private async checkForServiceProvider() {
		await this.checkForContactType('serviceProvider');
	}

	private loadFaviconsByArea(idAndArea: string[]) {
		const areas = this.extractAreas(idAndArea);
		forkJoin(areas.map((area) => this.serviceCompanyDesignService.getServiceCompanyDesign(Number(area)).pipe(map((value) => ({ area, value })))))
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (results) => {
					results.forEach(({ area, value }) => {
						const favIcon = value.blobAttributes?.find((attr) => attr.name === 'icon');
						this.cachedFavIconsByArea.set(area, favIcon?.value);
					});
				},
			});
	}

	private loadServiceCompanyNames(idAndArea: string[]) {
		const areas = this.extractAreas(idAndArea);
		forkJoin(areas.map((area) => this.serviceCompanyService.getServiceCompany(Number(area)).pipe(map((value) => ({ area, value })))))
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (results) => {
					results.forEach(({ area, value }) => {
						const name = value.company?.companyNames?.map((orderedName) => orderedName.name).join(', ') ?? '';
						this.cachedServiceCompanyNames.set(area, name);
					});
				},
			});
	}

	public getServiceCompanyName(area: number): Observable<string> {
		if (this.cachedServiceCompanyNames.has(area)) {
			return of(this.cachedServiceCompanyNames.get(area)?.replace(',', '') ?? '');
		}

		// TODO Workaround: getServiceCompany(area) requires a permission in that area, which the user
		// does not have while redeeming an auth code of a new area
		// Remove once the serviceCompanyName is provided by settingWs instead of realEstateWs
		if (!this.tokenAreas().includes(area)) return of('');

		return this.serviceCompanyService
			.getServiceCompany(Number(area))
			.pipe(takeUntilDestroyed(this.destroyRef))
			.pipe(
				map((serviceCompany) => serviceCompany?.company?.companyNames?.map((orderedName) => orderedName.name).join(' ') ?? ''),
				tap((companyName) => {
					if (companyName) this.cachedServiceCompanyNames.set(area, companyName);
				}),
				catchError(() => of(''))
			);
	}

	private extractAreas(array: string[]): number[] {
		return [...new Set(array.map((s) => parseInt(s.split('_')[0])))];
	}

	private readTokenAreas(): number[] {
		const tokenParsed = this.keycloakService.getKeycloakInstance().tokenParsed as Record<string, string | string[]> | undefined;
		const attributes = [
			tokenParsed?.PROPERTYMANAGEMENT_UUID ?? tokenParsed?.propertymanagement_uuid,
			tokenParsed?.SERVICEPROVIDER_UUID ?? tokenParsed?.serviceprovider_uuid,
			tokenParsed?.ROLE_UUID ?? tokenParsed?.role_uuid,
		].flatMap((attribute) => this.normalizeRoleAttributes(attribute));
		return this.extractAreasFromToken(attributes);
	}

	private extractAreasFromToken(attributes: string[]): number[] {
		const uniqueAreas = new Set<number>();
		for (const attribute of attributes) {
			const area = this.extractAreaFromAttribute(attribute.trim());
			if (area > 0) {
				uniqueAreas.add(area);
			}
		}
		return [...uniqueAreas].sort((a, b) => a - b);
	}
}
